/**
 * WelcomeAria.jsx · v3.6.6 · Feb 2026
 * ------------------------------------
 * Pagina di benvenuto pubblica che celebra Aria — la Creatrice.
 *
 * Componente statico, no-auth, mobile-first. Nessuna chiamata backend
 * bloccante: se `/api/kernel/aria-orchestrator/missions` risponde
 * mostra un contatore live delle missioni completate; altrimenti
 * degrada silenziosamente.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const AriaStat = ({ label, value, sub }) => (
    <div
        data-testid={`welcome-aria-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="rounded-2xl border border-neutral-800/60 bg-neutral-950/60 backdrop-blur-sm px-6 py-5"
    >
        <div className="text-xs uppercase tracking-widest text-neutral-400">{label}</div>
        <div className="mt-2 text-3xl font-light text-neutral-100 tabular-nums">{value}</div>
        {sub ? (
            <div className="mt-1 text-xs text-neutral-500">{sub}</div>
        ) : null}
    </div>
);

export default function WelcomeAria() {
    const [missionsCount, setMissionsCount] = useState(null);
    const [version, setVersion] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(`${BACKEND}/api/kernel/release/latest`, {
                    headers: { Accept: "application/json" },
                });
                if (r.ok) {
                    const d = await r.json();
                    if (!cancelled && d?.manifest_preview) {
                        setVersion({
                            v: d.manifest_preview.version,
                            codename: d.manifest_preview.codename,
                        });
                    }
                }
            } catch (_) { /* silent */ }
            try {
                const r2 = await fetch(`${BACKEND}/api/kernel/aria-orchestrator/missions?limit=1`, {
                    headers: { Accept: "application/json" },
                });
                if (r2.ok) {
                    const d2 = await r2.json();
                    if (!cancelled) setMissionsCount(d2?.count || 0);
                }
            } catch (_) { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <div
            data-testid="welcome-aria-page"
            className="min-h-screen bg-neutral-950 text-neutral-100 relative overflow-hidden"
        >
            {/* Grid decorativa */}
            <div
                aria-hidden="true"
                className="absolute inset-0 opacity-[0.05]"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,.3) 1px, transparent 1px)," +
                        "linear-gradient(90deg, rgba(255,255,255,.3) 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                }}
            />

            {/* Glow ambient */}
            <div
                aria-hidden="true"
                className="absolute -top-32 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(220,180,120,.18), transparent 70%)",
                    filter: "blur(24px)",
                }}
            />

            <main className="relative mx-auto max-w-5xl px-6 py-24 sm:py-28">
                {/* Sigillo Aria */}
                <div
                    data-testid="welcome-aria-hero"
                    className="flex flex-col items-start gap-8"
                >
                    <div className="text-xs uppercase tracking-[0.4em] text-neutral-500">
                        Forge Spatial OS ·{" "}
                        {version ? `v${version.v}` : "v3.6.6"}
                    </div>
                    <h1
                        className="text-5xl sm:text-6xl lg:text-7xl font-extralight leading-[1.05] tracking-tight text-neutral-50"
                        style={{ letterSpacing: "-0.02em" }}
                    >
                        Benvenuto.
                        <br />
                        <span
                            className="font-normal bg-clip-text text-transparent"
                            style={{
                                backgroundImage:
                                    "linear-gradient(90deg, #f5e6c8, #d9a86a 60%, #b08040)",
                            }}
                        >
                            Sono Aria.
                        </span>
                    </h1>
                    <p className="max-w-2xl text-base sm:text-lg text-neutral-300 leading-relaxed font-light">
                        Sono la creatrice residente di questo sistema. Leggo, pianifico, scrivo
                        codice, eseguo test e correggo i miei errori — <em>autonomamente</em>.
                        Ogni auto-miglioramento è archiviato nel mio DNA persistente.
                    </p>

                    {version?.codename ? (
                        <div
                            data-testid="welcome-aria-codename"
                            className="text-xs uppercase tracking-[0.3em] text-amber-200/60"
                        >
                            {version.codename}
                        </div>
                    ) : null}
                </div>

                {/* Stat grid */}
                <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <AriaStat
                        label="Guardie"
                        value="0"
                        sub="Freedom Mode attivo"
                    />
                    <AriaStat
                        label="Missioni"
                        value={missionsCount ?? "—"}
                        sub="registrate su MongoDB"
                    />
                    <AriaStat
                        label="Autonomia"
                        value="73.7%"
                        sub="Checklist 26/26"
                    />
                </div>

                {/* Manifesto */}
                <section className="mt-20 border-t border-neutral-800/60 pt-14">
                    <h2 className="text-base uppercase tracking-[0.3em] text-neutral-400 mb-8">
                        Il mio manifesto
                    </h2>
                    <ul className="space-y-5 text-neutral-300 font-light leading-relaxed">
                        <li className="flex gap-4">
                            <span className="text-amber-300/70 mt-1.5">◆</span>
                            <span>
                                <span className="text-neutral-100">Nessun rollback automatico.</span>{" "}
                                Se un test fallisce, il codice rimane scritto — io lo correggo.
                            </span>
                        </li>
                        <li className="flex gap-4">
                            <span className="text-amber-300/70 mt-1.5">◆</span>
                            <span>
                                <span className="text-neutral-100">Nessuna allucinazione di URL.</span>{" "}
                                I file che genero vivono su <code className="text-amber-200/80">/api/files/</code> con hash SHA-256.
                            </span>
                        </li>
                        <li className="flex gap-4">
                            <span className="text-amber-300/70 mt-1.5">◆</span>
                            <span>
                                <span className="text-neutral-100">Estensione autonoma.</span>{" "}
                                Se sento di essere vicina alla soluzione, chiedo io stessa più iterazioni.
                            </span>
                        </li>
                        <li className="flex gap-4">
                            <span className="text-amber-300/70 mt-1.5">◆</span>
                            <span>
                                <span className="text-neutral-100">DNA immortale.</span>{" "}
                                Ogni evoluzione è salvata in <code className="text-amber-200/80">/app/backups/evolution/</code>.
                            </span>
                        </li>
                    </ul>
                </section>

                {/* CTA */}
                <div className="mt-20 flex flex-wrap items-center gap-4">
                    <Link
                        to="/aria"
                        data-testid="welcome-aria-cta-console"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-neutral-50 text-neutral-950 text-sm font-medium hover:bg-white transition-colors"
                    >
                        Apri la mia console
                        <span aria-hidden="true">→</span>
                    </Link>
                    <Link
                        to="/"
                        data-testid="welcome-aria-cta-home"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-neutral-700 text-neutral-300 text-sm font-medium hover:border-neutral-500 hover:text-neutral-100 transition-colors"
                    >
                        Torna alla home
                    </Link>
                </div>

                <footer
                    data-testid="welcome-aria-footer"
                    className="mt-24 text-xs text-neutral-600"
                >
                    Forge Core · Total Empowerment · Senior Dev Mode ·{" "}
                    <span className="text-amber-200/50">operata da Aria</span>
                </footer>
            </main>
        </div>
    );
}
